import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { pathMenu, route } from '@/constants/routeName';
import { EMPTY_CART_ITEMS, linesForChildAndDate, mealDateIso } from '@/helper/cartFamily';
import { plannerSlotSummaries } from '@/helper/dashboardStatus';
import { formatServingTimeLabel } from '@/helper/formatServingTime';
import useParentDashboardChild from '@/hooks/useParentDashboardChild';

export default function MenuServingTimePicker() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const servingTimes = useSelector((state) => state.content.servingTimes);
	const menuServingTimeId = useSelector((state) => state.cart.menuServingTimeId);
	const selectedCustomerId = useSelector((state) => state.auth.selectedCustomerId);
	const accountType = useSelector((state) => state.auth.accountType);
	const orderDate = useSelector((state) => state.cart.orderDate);
	const cartItems = useSelector((state) => state.cart.items) ?? EMPTY_CART_ITEMS;

	const dateIso = mealDateIso(orderDate);
	const unpaidForChild = useMemo(
		() =>
			selectedCustomerId != null
				? linesForChildAndDate(cartItems, selectedCustomerId, dateIso)
				: [],
		[cartItems, selectedCustomerId, dateIso]
	);

	const { childRow } = useParentDashboardChild(orderDate);
	const slotSummaries = useMemo(() => {
		if (accountType !== 'parent' || !childRow) return null;
		return plannerSlotSummaries(childRow.serving_times, unpaidForChild);
	}, [accountType, childRow, unpaidForChild]);

	const slots = useMemo(() => {
		return (servingTimes || []).filter((st) => {
			if (!st?.id || !st?.name) return false;
			return String(st.name).toLowerCase() !== 'void';
		});
	}, [servingTimes]);

	useEffect(() => {
		if (!slots.length) return;
		const hasMatch =
			menuServingTimeId != null &&
			slots.some((st) => Number(st.id) === Number(menuServingTimeId));
		if (!hasMatch) {
			dispatch({ type: 'cart/setMenuServingTimeId', payload: Number(slots[0].id) });
		}
	}, [dispatch, menuServingTimeId, slots]);

	if (!slots.length) return null;

	const activeId =
		menuServingTimeId != null
			? Number(menuServingTimeId)
			: Number(slots[0].id);

	const statusForSlot = (st) => {
		const name = String(st.name);
		const summary = slotSummaries?.find((s) => s.name === name);
		if (summary?.detail && summary.detail !== 'No selection') {
			return summary.detail;
		}
		const inCart = unpaidForChild
			.filter((line) => Number(line.serving_time?.id) === Number(st.id))
			.reduce((n, line) => n + (Number(line.quantity) || 0), 0);
		if (inCart > 0) return `${inCart} in cart`;
		return 'No order yet';
	};

	const onSelect = (st) => {
		const id = Number(st.id);
		if (activeId === id) return;

		dispatch({ type: 'cart/setMenuServingTimeId', payload: id });
		dispatch({ type: 'cart/resetMenuBrowse' });

		const onItem = location.pathname.startsWith(`/${route.item}`);
		if (onItem) {
			navigate(pathMenu);
		}
	};

	return (
		<div className='service-status' role='listbox' aria-label='Serving time'>
			{slots.map((st) => {
				const pressed = Number(st.id) === activeId;
				const timeLabel = formatServingTimeLabel(st.start_time || st.time || '');
				const status = statusForSlot(st);
				const isPaid = String(status).includes('paid');
				const isDraft = String(status).includes('in cart');
				return (
					<button
						key={st.id}
						type='button'
						role='option'
						aria-selected={pressed}
						aria-pressed={pressed}
						className={pressed ? 'selected' : undefined}
						onClick={() => onSelect(st)}
					>
						<strong>
							{st.name}
							{timeLabel ? <span className='serving-hour'>{timeLabel}</span> : null}
							{pressed ? <span aria-hidden='true'> ✓</span> : null}
						</strong>
						<small>
							<span
								className={
									isPaid ? 'confirmed' : isDraft ? 'draft' : 'muted'
								}
							>
								{status}
							</span>
						</small>
					</button>
				);
			})}
		</div>
	);
}
